import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { qualityInspectionApi } from '../../api/qualityInspection';

// 平台确认收货
export const confirmPlatformReceipt = createAsyncThunk(
  'qualityInspection/confirmPlatformReceipt',
  async (data, { rejectWithValue }) => {
    try {
      const response = await qualityInspectionApi.confirmPlatformReceipt(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '平台确认收货失败' });
    }
  }
);

// 平台送检
export const sendForInspection = createAsyncThunk(
  'qualityInspection/sendForInspection',
  async (data, { rejectWithValue }) => {
    try {
      const response = await qualityInspectionApi.sendForInspection(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '平台送检失败' });
    }
  }
);

// 平台上传质检结果
export const uploadInspectionResult = createAsyncThunk(
  'qualityInspection/uploadInspectionResult',
  async (data, { rejectWithValue }) => {
    try {
      const response = await qualityInspectionApi.uploadInspectionResult(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '上传质检结果失败' });
    }
  }
);

// 分页查询质检列表
export const fetchQualityInspectionPage = createAsyncThunk(
  'qualityInspection/fetchPage',
  async ({ page = 1, pageSize = 10, queryParams = {} }, { rejectWithValue }) => {
    try {
      const response = await qualityInspectionApi.pageQualityInspections(page, pageSize, queryParams);
      
      // 处理按店铺分组的数据结构
      const processedData = {
        records: (response.data && response.data.records ? response.data.records : []).map(store => ({
          storeId: store.storeId || null,
          storeName: store.storeName || '',
          orders: (store.orders || []).map(order => ({
            orderId: order.orderId || null,
            orderStatus: order.orderStatus || null,
            orderUid: order.orderUid || null,
            qualityInspections: (order.qualityInspections || []).map(item => {
              const orderItem = item.orderItem || {};
              const qualityInspection = item.qualityInspection || {};
              
              return {
                // 订单项信息
                orderItemId: orderItem.id || null,
                orderId: orderItem.orderId || null,
                goodsId: orderItem.goodsId || null,
                goodsName: orderItem.goodsName || null,
                mainImage: orderItem.mainImage || null,
                goodsPrice: orderItem.goodsPrice || 0,
                goodsQuantity: orderItem.goodsQuantity || 0,
                totalPrice: orderItem.totalPrice || 0,
                specifications: orderItem.specifications || null,
                createTime: formatCreateTime(orderItem.createTime),
                updateTime: formatCreateTime(orderItem.updateTime),

                // 质检信息
                qualityInspectionId: qualityInspection.id || null,
                inspectionNo: qualityInspection.inspectionNo || null,
                inspectorId: qualityInspection.inspectorId || null,
                status: qualityInspection.status || null,
                startTime: formatCreateTime(qualityInspection.startTime),
                endTime: formatCreateTime(qualityInspection.endTime),
                qualityInspectionCreateTime: formatCreateTime(qualityInspection.createTime),
                qualityInspectionUpdateTime: formatCreateTime(qualityInspection.updateTime),

                // 质检结果
                qualityInspectionResult: item.qualityInspectionResult || null
              };
            })
          }))
        })),
        total: response.data && response.data.total ? response.data.total : 0,
        current: response.data && response.data.current ? response.data.current : page,
        size: response.data && response.data.size ? response.data.size : pageSize,
        pages: response.data && response.data.pages ? response.data.pages : 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response && error.response.data ? error.response.data : { message: '获取质检列表失败' });
    }
  }
);

// 分页查询质检列表（按订单分组）
export const fetchOrderGroupedQualityInspections = createAsyncThunk(
  'qualityInspection/fetchOrderGrouped',
  async (queryParams = null, { rejectWithValue }) => {
    try {
      const response = await qualityInspectionApi.pageOrderGroupedQualityInspections(queryParams);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取质检列表（按订单分组）失败' });
    }
  }
);

// 分页查询质检列表
export const fetchQualityInspectionList = createAsyncThunk(
  'qualityInspection/fetchList',
  async (queryParams = null, { rejectWithValue }) => {
    try {
      const response = await qualityInspectionApi.pageQualityInspectionList(queryParams);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取质检列表失败' });
    }
  }
);

// 辅助函数：将后端的时间数组转换为可读的日期字符串
function formatCreateTime(timeArray) {
  if (!Array.isArray(timeArray) || timeArray.length < 6) {
    return '';
  }
  // timeArray: [2025, 10, 4, 10, 12, 21]
  // 转换为 'YYYY-MM-DD HH:mm:ss' 格式
  return `${timeArray[0]}-${
    String(timeArray[1]).padStart(2, '0')
  }-${
    String(timeArray[2]).padStart(2, '0')
  } ${
    String(timeArray[3]).padStart(2, '0')
  }:${
    String(timeArray[4]).padStart(2, '0')
  }:${
    String(timeArray[5]).padStart(2, '0')
  }`;
}

const qualityInspectionSlice = createSlice({
  name: 'qualityInspection',
  initialState: {
    qualityInspections: [],
    orderGroupedQualityInspections: null,
    qualityInspectionList: null,
    total: 0,
    loading: false,
    error: null,
    currentPage: 1,
    pageSize: 10
  },
  reducers: {},
  extraReducers: (builder) => {
    // 分页查询质检列表
    builder.addCase(fetchQualityInspectionPage.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchQualityInspectionPage.fulfilled, (state, action) => {
      state.loading = false;
      state.qualityInspections = action.payload.records;
      state.total = action.payload.total;
      state.currentPage = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchQualityInspectionPage.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 分页查询质检列表（按订单分组）
    builder.addCase(fetchOrderGroupedQualityInspections.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchOrderGroupedQualityInspections.fulfilled, (state, action) => {
      state.loading = false;
      state.orderGroupedQualityInspections = action.payload;
      // 更新分页信息
      if (action.payload) {
        state.currentPage = action.payload.current || state.currentPage;
        state.pageSize = action.payload.size || state.pageSize;
        state.total = action.payload.total || state.total;
      }
    });
    builder.addCase(fetchOrderGroupedQualityInspections.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 分页查询质检列表
    builder.addCase(fetchQualityInspectionList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchQualityInspectionList.fulfilled, (state, action) => {
      state.loading = false;
      state.qualityInspectionList = action.payload;
      // 更新分页信息
      if (action.payload) {
        state.currentPage = action.payload.current || state.currentPage;
        state.pageSize = action.payload.size || state.pageSize;
        state.total = action.payload.total || state.total;
      }
    });
    builder.addCase(fetchQualityInspectionList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 平台确认收货
    builder.addCase(confirmPlatformReceipt.fulfilled, (state) => {
      // 可以在这里更新状态，例如重新获取列表
    });

    // 平台送检
    builder.addCase(sendForInspection.fulfilled, (state) => {
      // 可以在这里更新状态，例如重新获取列表
    });

    // 平台上传质检结果
    builder.addCase(uploadInspectionResult.fulfilled, (state) => {
      // 可以在这里更新状态，例如重新获取列表
    });
  }
});

export default qualityInspectionSlice.reducer;
